import { useState, useEffect } from 'react';
import { uploadDocument } from '../services/api';
import type { DuplicateCandidate } from '../services/api';
import { domainsService } from '../services/domainsService';
import { apiClient } from '../services/apiClient';
import type { Document } from '../types/document';
import ReuploadDedupModal from './documents/ReuploadDedupModal';
import type { UploadConflict, ConflictDecision } from './documents/ReuploadDedupModal';

interface Props {
  onUploadSuccess: (doc: Document) => void;
}

interface ActiveVersions {
  schema: { version: string } | null;
  prompt: { version: string } | null;
  llm: { version: string } | null;
  ocr: { version: string } | null;
}

export function DocumentUpload({ onUploadSuccess }: Props) {
  const [error, setError] = useState<string | null>(null);
  const [domain, setDomain] = useState<string>('');
  const [domains, setDomains] = useState<Array<{ value: string; label: string }>>([]);
  const [activeVersions, setActiveVersions] = useState<ActiveVersions>({ schema: null, prompt: null, llm: null, ocr: null });

  // PROD1.10 #671 — conflits de doublon (409) collectés sur le lot courant.
  const [conflicts, setConflicts] = useState<UploadConflict[]>([]);
  const [resolvingConflicts, setResolvingConflicts] = useState(false);

  // Load active schema and prompt for the selected domain
  useEffect(() => {
    if (!domain) return;

    const loadActiveVersions = async () => {
      try {
        const [schemaRes, promptRes, llmRes, ocrRes] = await Promise.allSettled([
          apiClient.get<any>(`/tenant/me/schemas/active/${domain}`).then(r => r.data),
          apiClient.get<any>(`/tenant/me/prompts/active/${domain}`).then(r => r.data),
          apiClient.get<any>(`/tenant/me/llm-configs/domains/${domain}/active`).then(r => r.data),
          apiClient.get<any>(`/tenant/me/ocr-configs/domains/${domain}/active`).then(r => r.data),
        ]);

        setActiveVersions({
          schema: schemaRes.status === 'fulfilled' ? schemaRes.value : null,
          prompt: promptRes.status === 'fulfilled' ? promptRes.value : null,
          llm: llmRes.status === 'fulfilled' ? llmRes.value : null,
          ocr: ocrRes.status === 'fulfilled' ? ocrRes.value : null,
        });
      } catch (err) {
        console.error('Failed to load active versions:', err);
      }
    };

    loadActiveVersions();
  }, [domain]);

  // Load active domains from API on mount (only active domains for document upload)
  useEffect(() => {
    const loadDomains = async () => {
      try {
        const domainOptions = await domainsService.getActiveDomainOptions();
        setDomains(domainOptions);
        // Set first domain as default if not already set
        if (domainOptions.length > 0 && !domain) {
          setDomain(domainOptions[0].value);
        }
      } catch (err) {
        console.error('Failed to load domains:', err);
        setError('Erreur lors du chargement des types de documents');
      }
    };
    loadDomains();
  }, []);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    if (!domain) {
      setError('Veuillez sélectionner un type de document');
      return;
    }

    // Validate all files are PDFs
    const invalidFiles = Array.from(files).filter(f => f.type !== 'application/pdf');
    if (invalidFiles.length > 0) {
      setError(`Seuls les fichiers PDF sont acceptés (${invalidFiles.length} fichier(s) ignoré(s))`);
    }

    const validFiles = Array.from(files).filter(f => f.type === 'application/pdf');
    if (validFiles.length === 0) return;

    setError(null);

    // Reset input so user can select more files right away
    e.target.value = '';

    void uploadBatch(validFiles);
  };

  /**
   * Upload un lot en parallèle. Les fichiers sans match → nouveaux docs.
   * Les réponses 409 « doublon de contenu » sont collectées dans une modale
   * récapitulative (pas une erreur). Les autres erreurs comptent comme échecs.
   */
  const uploadBatch = async (files: File[]) => {
    const results = await Promise.allSettled(files.map((file) => uploadDocument(file, domain)));

    let succeeded = 0;
    let failed = 0;
    const newConflicts: UploadConflict[] = [];

    results.forEach((result, i) => {
      if (result.status === 'fulfilled') {
        succeeded += 1;
        return;
      }
      const err = result.reason as Error & { status?: number; detail?: any };
      const candidates: DuplicateCandidate[] | undefined = err?.detail?.candidates;
      if (err?.status === 409 && err?.detail?.code === 'duplicate_content' && candidates?.length) {
        // On cible le candidat le plus récent (1er de la liste, triée DESC backend).
        newConflicts.push({ file: files[i], candidate: candidates[0] });
      } else {
        failed += 1;
      }
    });

    if (failed > 0) {
      setError(`${failed}/${files.length} upload(s) échoué(s)`);
    }
    if (succeeded > 0) {
      onUploadSuccess(null as any); // Trigger reload des nouveaux docs
    }
    if (newConflicts.length > 0) {
      setConflicts(newConflicts);
    }
  };

  /**
   * Confirmation de la modale : ré-extrait les fichiers marqués « Ré-extraire »,
   * ignore les autres. Recharge la liste après traitement.
   */
  const handleResolveConflicts = async (decisions: ConflictDecision[]) => {
    setResolvingConflicts(true);
    try {
      const toReextract = conflicts.filter((_, i) => decisions[i] === 'reextract');

      const results = await Promise.allSettled(
        toReextract.map((c) =>
          uploadDocument(c.file, domain, {
            onDuplicate: 'reextract',
            targetDocumentId: c.candidate.id,
          }),
        ),
      );

      const failed = results.filter((r) => r.status === 'rejected').length;
      if (failed > 0) {
        setError(`${failed}/${toReextract.length} ré-extraction(s) échouée(s)`);
      }

      // Recharge la liste si au moins une ré-extraction a été demandée.
      if (toReextract.length > 0) {
        onUploadSuccess(null as any);
      }
    } finally {
      setResolvingConflicts(false);
      setConflicts([]);
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6">
      <div className="flex items-center gap-6">
        {/* Domain selector */}
        <div className="flex items-center gap-2">
          <label htmlFor="domain-select" className="text-sm font-medium text-gray-700 whitespace-nowrap">
            Type de document
          </label>
          <select
            id="domain-select"
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
          >
            {domains.length === 0 && <option value="">Chargement...</option>}
            {domains.map((d, index) => (
              <option key={`${d.value}-${index}`} value={d.value}>{d.label}</option>
            ))}
          </select>
        </div>

        {/* File upload button */}
        <div>
          <input
            type="file"
            accept=".pdf"
            multiple
            onChange={handleFileChange}
            className="hidden"
            id="file-upload"
          />
          <label
            htmlFor="file-upload"
            className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-600 text-sm font-medium"
          >
            Sélectionner PDF(s)
          </label>
        </div>

        {/* Active versions display */}
        {domain && (
          <div className="flex items-center gap-3 text-sm text-gray-600 ml-auto">
            <span>
              Schéma : <span className="font-medium text-gray-800">v {activeVersions.schema?.version || '-'}</span>
            </span>
            <span className="text-gray-300">|</span>
            <span>
              Prompt : <span className="font-medium text-gray-800">v {activeVersions.prompt?.version || '-'}</span>
            </span>
            <span className="text-gray-300">|</span>
            <span>
              LLM : <span className="font-medium text-gray-800">v {(activeVersions.llm as any)?.version || '-'}</span>
            </span>
            <span className="text-gray-300">|</span>
            <span>
              OCR : <span className="font-medium text-gray-800">v {(activeVersions.ocr as any)?.version || '-'}</span>
            </span>
          </div>
        )}
      </div>

      {error && <p className="mt-2 text-red-600 text-sm">{error}</p>}

      {/* PROD1.10 #671 — modale récapitulative des doublons de contenu */}
      <ReuploadDedupModal
        open={conflicts.length > 0}
        conflicts={conflicts}
        submitting={resolvingConflicts}
        onConfirm={handleResolveConflicts}
        onCancel={() => setConflicts([])}
      />
    </div>
  );
}
